import { IMAGE_CONFIG, IMAGE_LOADER } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  signal,
} from '@angular/core';
import {
  getApp,
  initializeApp,
  provideFirebaseApp,
} from '@jfw/angular/shared/firebase/app';
import {
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaEnterpriseProvider,
} from '@jfw/angular/shared/firebase/app-check';
import { getAuth, provideAuth } from '@jfw/angular/shared/firebase/auth';
import {
  getFirestore,
  provideFirestore,
} from '@jfw/angular/shared/firebase/firestore';
import {
  getPerformance,
  providePerformance,
} from '@jfw/angular/shared/firebase/performance';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  ECOM_DP_ENVIRONMENT,
  imageLoaderFactory,
} from '@jfw/angular/ecommerce/core';
import { ECOM_DEALER_USER_SESSION_API_URL } from '@jfw/angular/ecommerce/api-services';
import { appCheckInterceptor } from '@jfw/angular/shared/app-check';
import {
  FEEDBACK_DIALOG_HOST,
  FeedbackActivityRecorder,
  FeedbackErrorHandler,
  FeedbackErrorInterceptor,
} from '@jfw/angular/shared/common-components/feedback';
import {
  IS_IT_DEPARTMENT_USER,
  PLATFORM_DP_ENVIRONMENT,
  SHARED_ENVIRONMENT,
} from '@jfw/angular/shared/tokens';
import { provideEnglishOnlyTransloco } from '@jfw/angular/shared/i18n';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TokenInterceptor } from './interceptors/token/token.interceptor';
import { DealerFeedbackDialogHost } from './services/feedback/dealer-feedback-dialog-host.service';

const interceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
  // EXT-6239: failed calls feed the feedback dialog's "recent errors"
  // context. Passes everything through unchanged.
  {
    provide: HTTP_INTERCEPTORS,
    useClass: FeedbackErrorInterceptor,
    multi: true,
  },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(),
    provideNativeDateAdapter(),
    // EXT-5701: shared jfw-library components render through Transloco.
    ...provideEnglishOnlyTransloco(),
    provideFirebaseApp(() =>
      initializeApp(environment.firebaseConfig, '[DEFAULT]'),
    ),
    // Platform connection is disabled for now.
    // Will re-enable if DealerAccounts SDK is needed in future.
    // provideFirebaseApp(() =>
    //   initializeApp(environment.platformFirebaseConfig, '[PLATFORM]'),
    // ),
    provideAuth(() => getAuth()),
    providePerformance(() => getPerformance()),
    provideFirestore(() => getFirestore()),
    provideAppCheck(() =>
      initializeAppCheck(getApp(), {
        provider: new ReCaptchaEnterpriseProvider(
          environment.recaptchaAppCheckKey,
        ),
        isTokenAutoRefreshEnabled: false, // this is also the default
      }),
    ),
    {
      provide: IMAGE_CONFIG,
      useValue: {
        breakpoints: [16, 48, 96, 128, 384, 640, 750, 828, 1080, 1200, 1920],
      },
    },
    {
      provide: IMAGE_LOADER,
      useFactory: imageLoaderFactory,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([appCheckInterceptor]),
    ),
    {
      provide: ECOM_DP_ENVIRONMENT, // used by jfw-library/ecommerce components and services
      useValue: environment,
    },
    {
      provide: PLATFORM_DP_ENVIRONMENT, // used by jfw-library components and services shared between Platform/Dealer Portal
      useValue: environment,
    },
    {
      provide: SHARED_ENVIRONMENT, // used by jfw-library/ecommerce/shared services
      useValue: environment,
    },
    {
      provide: ECOM_DEALER_USER_SESSION_API_URL, // EXT-6278: sign-in calls on the accounts container
      useValue: environment.dealer_user_api_server_url,
    },
    {
      // EXT-5689: dealer accounts have no department concept at all (external, not platform
      // staff) — always false, so the dev-only order-comparison harness button never shows here
      // regardless of environment.enableOrderComparisonHarness.
      provide: IS_IT_DEPARTMENT_USER,
      useValue: signal(false),
    },
    interceptorProviders,
    // EXT-6239: the header feedback dialog. Uncaught errors feed its context,
    // and the recorder is constructed at startup so route history covers the
    // pages visited before the dialog is first opened.
    { provide: ErrorHandler, useClass: FeedbackErrorHandler },
    provideAppInitializer(() => {
      inject(FeedbackActivityRecorder);
    }),
    { provide: FEEDBACK_DIALOG_HOST, useExisting: DealerFeedbackDialogHost },
  ],
};
